import { Alert, Button, Divider, Stack, Typography } from '@mui/material'
import React from 'react'
import DocLink from '../../components/DocLink'
import { FilterAlt, PersonAddAlt1 } from '@mui/icons-material'

const Patients = () => {
  return (
    <Stack gap={3} divider={<Divider />}>
      <Stack gap={1}>
        <Typography variant="title3">Patient Lookup</Typography>
        <Typography>
          To lookup an existing patient, head over to{' '}
          <DocLink url="/pts/patients" text="PTS -> Patients." />
          You'll need to use your two-factor authentication to access patient
          data.
        </Typography>
        <Typography>
          To search for a patient, you'll need a minimum of either:
        </Typography>
        <ol style={{ marginTop: 0 }}>
          <li>
            <strong>NHS Number</strong>
            <br />
            <i>An NHS number alone is sufficient</i>
          </li>
          <li>
            <strong>Demographics</strong>
            <br />
            <i>minimum of:</i>
            <br />
            <ul>
              <li>Surname</li>
              <li>Date of Birth</li>
            </ul>
          </li>
        </ol>
        <Typography>
          Once the patient record you require is listed, click on it to view
          more detail
        </Typography>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Create a new Patient Record</Typography>
        <Typography>
          To add a new patient, head to{' '}
          <DocLink url="/pts/patients" text="PTS -> Patients" />
          and click{' '}
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAddAlt1 />}
            color="contrast"
            sx={{
              display: 'inline-flex',
              verticalAlign: 'flex-end',
              ml: 0.5,
            }}
          >
            Create a New Patient Record
          </Button>
        </Typography>
        <Typography>
          As a minimum, you will need to include a First Name, Last Name, Date
          of Birth, Address, and a Mobility category.
        </Typography>
        <Alert severity="info" variant="outlined">
          Tenants using the <strong>reduced minimum dataset</strong>{' '}
          (Configuration &gt; Patient Transport &gt; PTS Bookings) need only a
          first and last name — everything else can be added later. Missing
          clinical details are then flagged as outstanding on the patient's
          bookings and must be completed before a journey can be closed. See
          the{' '}
          <DocLink
            url="/patient-transport/bookings"
            text="Bookings docs"
            docsPage
          />{' '}
          for how this works.
        </Alert>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Patient Demographics</Typography>
        <Typography>
          The Patient Demographics require a First Name, Last Name, Date of
          Birth, and Address as a minimum (name only on reduced-dataset
          tenants). An NHS number is never mandatory — when present it must
          be unique, and duplicates are flagged before a new record is
          created.
        </Typography>
        <Typography>
          If mobile numbers are recorded, both the patient's phone number and
          alternative phone number may be used for journey notification SMS
          messages, including the live tracking link sent once a crew is on
          the way — see{' '}
          <DocLink
            url="/patient-transport/live-tracking"
            text="Live Journey Tracking"
            docsPage
          />
          . Whether this SMS is sent at all is controlled by your
          organisation's PTS settings.
        </Typography>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Mobility</Typography>
        <Typography>
          Mobility category (Walking, Chair, or Stretcher) is required for
          every patient, since it's what tells booking and dispatch which type
          of vehicle to send. Patient records created before this was required
          show a warning on the booking screen if their mobility category is
          still blank, without blocking the booking.
        </Typography>
        <Typography>
          These options enable alerts when booking patients into duties and
          routes. Bariatric patients should be assigned to vehicles capable of
          bariatric transport, and booking staff will be warned if assigning a
          bariatric patient to an unsuitable vehicle.
        </Typography>
        <Typography>
          The Mobility section also records the patient's{' '}
          <strong>height (cm)</strong> and <strong>weight (kg)</strong> —
          useful for choosing the right crew and vehicle, especially on
          repeat transfers. Both are optional and shown wherever the patient's
          mobility appears: the booking form's patient card, the booking page,
          the patient page, and the crew's journey details on the mobile app.
          If a weight is recorded and the planned vehicle has a maximum
          patient weight set in its Fleet PTS configuration, planners are
          warned when the patient exceeds it — just like the bariatric check.
        </Typography>
        <Alert severity="info" variant="outlined">
          Patients who <strong>must travel alone</strong> will not be able to
          have their journey assigned to a route with another patient.
        </Alert>
        <Typography>
          You can add custom mobility aids for the patient by typing your own
          value into the text box and selecting "<strong>add</strong>" from the
          dropdown.
        </Typography>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Clinical / Risk Factors</Typography>
        <Typography>
          All clinical / risk factors recorded here will be shared with PTS
          booking staff, PTS ambulance crews, and hospital/organisation staff.
          They are also included in patient & journey summaries.
        </Typography>
        <Typography>
          For a considered, structured record of transport risk — per-domain
          ratings, transport requirements, discharge notes, follow-up actions,
          and controlled sharing with the hospital portal — use the patient's{' '}
          <strong>Risk Assessments</strong> card instead; see{' '}
          <DocLink
            url="/patient-transport/risk-assessments"
            text="Risk Assessments"
            docsPage
          />
          .
        </Typography>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Logs</Typography>
        <Typography>
          By default, the logs section shows changes to the patient record only.
          To see access logs, use the <FilterAlt fontSize="small" /> button at
          the top-right of the Logs card.
        </Typography>
      </Stack>
    </Stack>
  )
}

export default Patients
